import React, { useMemo, useState } from "react";
import Badge from "../../../library/Badge";
import IconButton from "../../../library/IconButton";
import NumberInput from "../../../library/NumberInput";
import Pagination from "../../../library/Pagination";
import { tokens } from "../../../../public/tokens";
import {
    getCreditIcon,
    getCreditTone,
    getRowsPerPageOptions,
    normalizeModuleRows,
    STANDARD_ROWS_PER_PAGE_SIZES,
} from "./studyPaths.utils";
import {
    StyledBandButton,
    StyledCreditsInputWrap,
    StyledDataSortIcon,
    StyledDataTable,
    StyledDataTd,
    StyledDataTh,
    StyledGroupBandCell,
    StyledGroupBandControls,
    StyledGroupBandLeft,
    StyledGroupBandRow,
    StyledGroupName,
    StyledModuleLink,
    StyledMaxCreditsLabel,
    StyledRowActions,
    StyledTableCard,
    StyledTableMessageCell,
    StyledTableWrapper,
    StyledWarningDot,
} from "./studyPaths.styles";

// SD-3 content-fits every column but Name, each with 8px of slack beside its sort icon, so these
// are fixed widths rather than shares of the design's 1322px canvas — a percentage collapses them
// at a narrower viewport and the icons overrun the next column. Duration/Credits are rounded a few
// px above the design (93.5/85.5) because the app never actually loads Albert Sans, so the
// fallback font's text is wider than Figma measures. Name absorbs the difference.
const COLUMNS = [
    { key: "name", label: "Name" },
    { key: "code", label: "Code", width: "104px" },
    { key: "duration", label: "Duration", width: "100px" },
    { key: "credits", label: "Credits", width: "92px" },
    { key: "action", label: "Action", width: "104px", sortable: false },
];

/**
 * One group's band row + module rows (+ optional pagination), as its own `<tbody>` — its own
 * component so per-group pagination state stays isolated even though sorting is now driven by
 * the shared header above (SPEC1 R3/SD-1 has no group controls or pagination; SD-3's phase form
 * has group controls but no pagination — see `showGroupControls`/`showPagination` on the parent).
 */
function ModuleGroupBody({
    group,
    modules,
    loading,
    suppliedCredits,
    requiredCredits,
    creditError,
    onRequiredCreditsChange,
    onAssignModule,
    onOpenModule,
    onUnlinkModule,
    showGroupControls,
    hideEmptyGroups,
    showPagination,
    creditsDisabled,
    creditsDisabledReason,
    assignDisabled,
    assignDisabledReason,
}) {
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(STANDARD_ROWS_PER_PAGE_SIZES[0]);

    const totalPages = Math.max(1, Math.ceil(modules.length / rowsPerPage));
    const currentPage = Math.min(page, totalPages);
    const pageModules = showPagination
        ? modules.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
        : modules;

    if (hideEmptyGroups && !loading && modules.length === 0) {
        return null;
    }

    const creditTone = getCreditTone(requiredCredits, suppliedCredits);

    return (
        <tbody>
            <tr>
                <StyledGroupBandCell colSpan={COLUMNS.length}>
                    <StyledGroupBandRow>
                        <StyledGroupBandLeft>
                            <StyledGroupName>{group.label}</StyledGroupName>
                            {creditTone && (
                                <Badge tone={creditTone} icon={getCreditIcon(creditTone)}>
                                    Credits: {Number(requiredCredits) || 0}/{suppliedCredits}
                                </Badge>
                            )}
                        </StyledGroupBandLeft>

                        {showGroupControls && (
                            <StyledGroupBandControls>
                                <StyledMaxCreditsLabel>Maximum Credits: {suppliedCredits}</StyledMaxCreditsLabel>
                                <StyledCreditsInputWrap>
                                    <NumberInput
                                        aria-label={`Required credits for ${group.label}`}
                                        value={requiredCredits}
                                        onChange={(event) => onRequiredCreditsChange(group.key, event.target.value)}
                                        min={0}
                                        disabled={creditsDisabled}
                                        disabledReason={creditsDisabledReason}
                                        errorText={creditError}
                                        placeholder="Credits Required"
                                    />
                                </StyledCreditsInputWrap>
                                <StyledBandButton
                                    type="button"
                                    colorOverride={tokens.colors.text.primary}
                                    onClick={() => onAssignModule(group)}
                                    disabled={assignDisabled}
                                    title={assignDisabled ? assignDisabledReason : undefined}
                                >
                                    <i className="fal fa-plus" aria-hidden="true" /> Assign Module
                                </StyledBandButton>
                            </StyledGroupBandControls>
                        )}
                    </StyledGroupBandRow>
                </StyledGroupBandCell>
            </tr>

            {loading ? (
                <tr>
                    <StyledTableMessageCell colSpan={COLUMNS.length}>Loading modules...</StyledTableMessageCell>
                </tr>
            ) : modules.length === 0 ? (
                <tr>
                    <StyledTableMessageCell colSpan={COLUMNS.length}>No modules in this group yet.</StyledTableMessageCell>
                </tr>
            ) : pageModules.map((module) => (
                <tr key={module.id}>
                    <StyledDataTd>
                        <StyledModuleLink
                            href={`/program-management-center/modules/module/open/${module.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {module.name}
                        </StyledModuleLink>
                        {module.hasWarning && (
                            <StyledWarningDot className="fal fa-exclamation-circle" aria-hidden="true" title="Flagged module" />
                        )}
                    </StyledDataTd>
                    <StyledDataTd>{module.code}</StyledDataTd>
                    <StyledDataTd>{module.duration ? `${module.duration} Weeks` : "-"}</StyledDataTd>
                    <StyledDataTd>{module.credits}</StyledDataTd>
                    <StyledDataTd>
                        <StyledRowActions>
                            <IconButton
                                icon="folder-open"
                                ariaLabel="Open module"
                                title={`Open ${module.name}`}
                                variant="solid"
                                onClick={() => onOpenModule(module)}
                            />
                            <IconButton
                                icon="unlink"
                                ariaLabel="Remove module from this phase"
                                title={`Remove ${module.name} from ${group.label}`}
                                variant="destructive"
                                disabled={assignDisabled}
                                disabledReason={assignDisabledReason}
                                onClick={() => onUnlinkModule(module)}
                            />
                        </StyledRowActions>
                    </StyledDataTd>
                </tr>
            ))}

            {showPagination && modules.length > STANDARD_ROWS_PER_PAGE_SIZES[0] && (
                <tr>
                    <td colSpan={COLUMNS.length}>
                        <Pagination
                            totalEntries={modules.length}
                            rangeStart={(currentPage - 1) * rowsPerPage + 1}
                            rangeEnd={Math.min(currentPage * rowsPerPage, modules.length)}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={getRowsPerPageOptions(modules.length)}
                            onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(1); }}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setPage}
                        />
                    </td>
                </tr>
            )}
        </tbody>
    );
}

/**
 * SPEC3 R2 — one shared header, then per group a band row (name/credits/Assign Module) and its
 * module rows. Also used by SPEC1 R3 (SD-1) with controls/pagination toggled per prop below.
 * @param {Array} props.groups One `{ group, modules, loading, suppliedCredits }` per
 *   `MODULE_GROUPS` entry. Also: requiredCredits, creditErrors (both keyed by group key).
 * @param {boolean} [props.showGroupControls=true] Shows Maximum Credits/input/Assign Module.
 * @param {boolean} [props.hideEmptyGroups=false] Skips a group entirely once it has no modules,
 *   instead of showing "No modules in this group yet."
 * @param {boolean} [props.showPagination=false] Paginates a group's rows past the standard page size.
 */
function PhaseModuleGroupsTable({
    groups,
    requiredCredits,
    creditErrors = {},
    onRequiredCreditsChange,
    onAssignModule,
    onOpenModule,
    onUnlinkModule,
    creditsDisabled = false,
    creditsDisabledReason,
    assignDisabled = false,
    assignDisabledReason,
    showGroupControls = true,
    hideEmptyGroups = false,
    showPagination = false,
}) {
    const [sortKey, setSortKey] = useState("name");
    const [sortAsc, setSortAsc] = useState(true);

    const sortedGroups = useMemo(
        () => groups.map((entry) => {
            const modules = normalizeModuleRows(entry.modules || []);
            const sorted = [...modules].sort((left, right) => (
                String(left[sortKey] ?? "").localeCompare(String(right[sortKey] ?? ""), undefined, { numeric: true })
            ));
            return {
                ...entry,
                modules: sortAsc ? sorted : sorted.reverse(),
            };
        }),
        [groups, sortKey, sortAsc]
    );

    const handleSort = (columnKey) => {
        if (columnKey === sortKey) {
            setSortAsc((current) => !current);
            return;
        }
        setSortKey(columnKey);
        setSortAsc(true);
    };

    return (
        <StyledTableCard>
            <StyledTableWrapper>
                <StyledDataTable>
                    <thead>
                        <tr>
                            {COLUMNS.map((column) => (
                                <StyledDataTh
                                    key={column.key}
                                    $width={column.width}
                                    $sortable={column.sortable !== false}
                                    onClick={column.sortable === false ? undefined : () => handleSort(column.key)}
                                >
                                    {column.label}
                                    {column.sortable !== false && (
                                        <StyledDataSortIcon
                                            $active={sortKey === column.key}
                                            className={`fal ${sortKey === column.key ? `fa-angle-${sortAsc ? "up" : "down"}` : "fa-angles-up-down"}`}
                                            aria-hidden="true"
                                        />
                                    )}
                                </StyledDataTh>
                            ))}
                        </tr>
                    </thead>

                    {sortedGroups.map(({ group, modules, loading, suppliedCredits }) => (
                        <ModuleGroupBody
                            key={group.key}
                            group={group}
                            modules={modules}
                            loading={loading}
                            suppliedCredits={suppliedCredits}
                            requiredCredits={requiredCredits[group.key]}
                            creditError={creditErrors[group.key]}
                            onRequiredCreditsChange={onRequiredCreditsChange}
                            onAssignModule={onAssignModule}
                            onOpenModule={onOpenModule}
                            onUnlinkModule={onUnlinkModule}
                            showGroupControls={showGroupControls}
                            hideEmptyGroups={hideEmptyGroups}
                            showPagination={showPagination}
                            creditsDisabled={creditsDisabled}
                            creditsDisabledReason={creditsDisabledReason}
                            assignDisabled={assignDisabled}
                            assignDisabledReason={assignDisabledReason}
                        />
                    ))}
                </StyledDataTable>
            </StyledTableWrapper>
        </StyledTableCard>
    );
}

PhaseModuleGroupsTable.displayName = "PhaseModuleGroupsTable";

export default PhaseModuleGroupsTable;
